import type { FunctionComponent } from "preact";
import { useCallback, useEffect, useRef, useState } from "preact/hooks";
import { getAllPhotos, type PhotoInfo, getPhotoImageData, getPhotoImageURL, getPhotos } from "../../store/task/photos.ts";
import { Icon } from "../../components/icon/icon.tsx";
import { useTranslator } from "../../hooks/i18n.ts";

interface PhotoTimelineProps {
    cameraId: number;
    onSelectPhoto: (photo: PhotoInfo, target: 0 | 1) => void;
    selectedLeftPhotoId?: number;
    selectedRightPhotoId?: number;
}

const formatTime = (timestamp: number) => {
    const date = new Date(timestamp * 1000);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

const formatDate = (timestamp: number) => {
    const date = new Date(timestamp * 1000);
    return date.toLocaleDateString([], { year: 'numeric', month: 'long', day: 'numeric' });
};

export const PhotoTimeline: FunctionComponent<PhotoTimelineProps> = ({ cameraId, onSelectPhoto, selectedLeftPhotoId, selectedRightPhotoId }) => {
    const [photos, setPhotos] = useState<PhotoInfo[]>([]);
    const [loading, setLoading] = useState(false);
    const [selectedPhotoId, setSelectedPhotoId] = useState<number | null>(null);
    const [selectedDate, setSelectedDate] = useState<string>("");
    const [isNavigating, setIsNavigating] = useState(false);
    const scrollRef = useRef<HTMLDivElement>(null);
    const itemRefs = useRef(new Map<number, HTMLDivElement>());
    const t = useTranslator();

    const fetchPhotos = useCallback(async () => {
        // Only skip if cameraId is invalid (e.g. -1 or 0)
        // But if it's 0, backend might not like it or return empty.
        // Let's ensure we have a valid camera ID before fetching.
        if (cameraId <= 0) {
            setPhotos([]); // Clear photos if invalid camera
            return;
        }
        
        setLoading(true);
        try {
            let result: PhotoInfo[] = [];
            
            if (selectedDate) {
                 // Calculate range for selected date (local time)
                 // selectedDate is "YYYY-MM-DD"
                 const [year, month, day] = selectedDate.split('-').map(Number);
                 
                 // Create date in local timezone
                 const start = new Date(year, month - 1, day, 0, 0, 0, 0);
                 const end = new Date(year, month - 1, day, 23, 59, 59, 999);
                 
                 // API expects seconds
                 const before = Math.floor(end.getTime() / 1000);
                 const after = Math.floor(start.getTime() / 1000);
                 
                 // Fetch more photos if specific date is selected (e.g., 500)
                 result = await getAllPhotos(cameraId, before, after, 500);
            } else {
                 // Default: last 50 photos
                 result = await getAllPhotos(cameraId, undefined, undefined, 50);
            }

            if (result && result.length > 0) {
                setPhotos([...result].sort((a, b) => b.photo_time - a.photo_time));
            } else {
                setPhotos([]);
            }
        } catch (e) {
            console.error("Failed to fetch timeline photos", e);
            // Don't clear photos on error to keep previous state if transient
        } finally {
            setLoading(false);
        }
    }, [cameraId, selectedDate]);

    useEffect(() => {
        if (!selectedPhotoId) return;
        const controller = new AbortController();
        getPhotoImageData(selectedPhotoId, "video", controller.signal).catch(() => {});
        return () => controller.abort();
    }, [selectedPhotoId]);

    useEffect(() => {
        fetchPhotos();
    }, [fetchPhotos]);

    useEffect(() => {
        const focusId = selectedLeftPhotoId ?? selectedRightPhotoId ?? null;
        if (!focusId) return;
        setSelectedPhotoId(focusId);
        const elem = itemRefs.current.get(focusId) ?? null;
        if (!elem) return;
        elem.scrollIntoView({ block: "nearest", inline: "start" });
    }, [selectedLeftPhotoId, selectedRightPhotoId, photos.length]);

    // Group photos by date
    const groupedPhotos = photos.reduce((acc, photo) => {
        const dateStr = formatDate(photo.photo_time);
        if (!acc[dateStr]) {
            acc[dateStr] = [];
        }
        acc[dateStr].push(photo);
        return acc;
    }, {} as Record<string, PhotoInfo[]>);

    // Helper to jump to available date
    const jumpToDate = useCallback(async (direction: -1 | 1) => {
        if (!cameraId) return;
        
        // Determine reference timestamp
        let referenceTime: number;
        if (selectedDate) {
            const [y, m, d] = selectedDate.split('-').map(Number);
            const date = new Date(y, m - 1, d);
            if (direction === -1) {
                 // Search before Start of Day
                 date.setHours(0,0,0,0);
                 referenceTime = Math.floor(date.getTime() / 1000);
            } else {
                 // Search after End of Day
                 date.setHours(23,59,59,999);
                 referenceTime = Math.floor(date.getTime() / 1000);
            }
        } else {
            // If "Latest" is selected
            if (direction === 1) return; // Can't go future
            // For previous, search before now
            referenceTime = Math.floor(Date.now() / 1000);
        }
        
        setIsNavigating(true);
        try {
            let result: PhotoInfo[] = [];
            if (direction === -1) {
                // Find latest photo BEFORE referenceTime
                // getPhotos(camId, before=referenceTime, after=0, limit=1, reverse=false)
                result = await getPhotos(cameraId, referenceTime, 0, 1, false);
            } else {
                 // Find earliest photo AFTER referenceTime
                 // getPhotos(camId, before=now, after=referenceTime, limit=1, reverse=true)
                 result = await getPhotos(cameraId, undefined, referenceTime, 1, true);
            }
            
            if (result.length > 0) {
                const photoTime = result[0].photo_time;
                const newDate = new Date(photoTime * 1000);
                const y = newDate.getFullYear();
                const m = String(newDate.getMonth() + 1).padStart(2, '0');
                const d = String(newDate.getDate()).padStart(2, '0');
                setSelectedDate(`${y}-${m}-${d}`);
            } else {
                // Could add toast here
                console.log("No photos found in that direction");
            }
        } catch (e) {
            console.error("Smart jump failed", e);
        } finally {
            setIsNavigating(false);
        }
    }, [selectedDate, cameraId]);

    // Smart Date Formatter
    const getSmartDateString = useCallback((dateStr: string) => {
        if (!dateStr) return t({ zh: "最新照片", en: "Latest Photos" });
        
        const now = new Date();
        const [y, m, d] = dateStr.split('-').map(Number);
        const date = new Date(y, m - 1, d);
        
        // Reset hours for comparison
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const target = new Date(date.getFullYear(), date.getMonth(), date.getDate());
        
        const diffTime = today.getTime() - target.getTime();
        const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));
        
        const weekday = date.toLocaleDateString(t({ zh: 'zh-CN', en: 'en-US' }), { weekday: 'short' });
        const datePart = date.toLocaleDateString(t({ zh: 'zh-CN', en: 'en-US' }), { month: 'short', day: 'numeric' });
        
        if (diffDays === 0) {
            return `${t({ zh: "今天", en: "Today" })} ${datePart} ${weekday}`;
        } else if (diffDays === 1) {
            return `${t({ zh: "昨天", en: "Yesterday" })} ${datePart} ${weekday}`;
        } else if (diffDays === 2) {
            return `${t({ zh: "前天", en: "2 Days Ago" })} ${datePart} ${weekday}`;
        } else {
            return `${dateStr} ${weekday}`;
        }
    }, [t]);

    return (
        <div class="farpov_timeline_container">
            <div class="farpov_timeline_controls" style={{
                padding: "0.5rem 1rem",
                display: "flex",
                gap: "0.75rem",
                alignItems: "center",
                borderBottom: "1px solid var(--pico-muted-border-color)",
                fontSize: "0.85rem",
                color: "var(--pico-muted-color)",
                flexShrink: 0,
                flexWrap: "wrap"
            }}>
                <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                    <Icon name="calendar" size={14} />
                    <span style={{ whiteSpace: "nowrap" }}>{t({ zh: "日期", en: "Date" })}:</span>
                </div>
                
                {/* Prev Available Day Button */}
                <button 
                    onClick={() => jumpToDate(-1)}
                    disabled={isNavigating}
                    title={t({ zh: "上一天 (有数据)", en: "Prev Available Day" })}
                    style={{
                        background: "var(--pico-card-background-color)",
                        border: "1px solid var(--pico-muted-border-color)",
                        borderRadius: "4px",
                        color: "var(--pico-primary)",
                        width: "28px",
                        height: "28px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                        padding: 0,
                        opacity: isNavigating ? 0.5 : 1
                    }}
                >
                    <Icon name="chevron-left" size={18} />
                </button>

                {/* Smart Date Display (Clickable to trigger hidden input) */}
                <div style={{ position: "relative", display: "inline-block", height: "28px" }}>
                    <div style={{
                        background: "var(--pico-card-background-color)",
                        border: "1px solid var(--pico-muted-border-color)",
                        borderRadius: "4px",
                        color: "var(--pico-color)",
                        padding: "0 0.75rem",
                        fontSize: "0.85rem",
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        minWidth: "160px",
                        whiteSpace: "nowrap",
                        cursor: "pointer",
                        fontWeight: 500
                    }}>
                        {getSmartDateString(selectedDate)}
                        <Icon name="chevron-down" size={14} style={{ marginLeft: "0.5rem", opacity: 0.5 }} />
                    </div>
                    
                    <input 
                        type="date" 
                        value={selectedDate} 
                        onChange={(e) => setSelectedDate(e.currentTarget.value)}
                        style={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            width: "100%",
                            height: "100%",
                            opacity: 0,
                            cursor: "pointer",
                            zIndex: 10
                        }}
                    />
                </div>

                {/* Next Available Day Button */}
                <button 
                    onClick={() => jumpToDate(1)}
                    disabled={isNavigating}
                    title={t({ zh: "下一天 (有数据)", en: "Next Available Day" })}
                    style={{
                        background: "var(--pico-card-background-color)",
                        border: "1px solid var(--pico-muted-border-color)",
                        borderRadius: "4px",
                        color: "var(--pico-primary)",
                        width: "28px",
                        height: "28px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                        padding: 0,
                        opacity: isNavigating ? 0.5 : 1
                    }}
                >
                    <Icon name="chevron-right" size={18} />
                </button>
                
                {/* Quick Actions */}
                <button 
                    onClick={() => {
                        const now = new Date();
                        const y = now.getFullYear();
                        const m = String(now.getMonth() + 1).padStart(2, '0');
                        const d = String(now.getDate()).padStart(2, '0');
                        setSelectedDate(`${y}-${m}-${d}`);
                    }} 
                    style={{
                        background: "transparent",
                        border: "none",
                        color: "var(--pico-primary)",
                        cursor: "pointer",
                        fontSize: "0.8rem",
                        textDecoration: "underline",
                        padding: 0,
                        marginLeft: "0.5rem"
                    }}
                >
                    {t({ zh: "今天", en: "Today" })}
                </button>

                {selectedDate && (
                    <button 
                        onClick={() => setSelectedDate("")}
                        style={{
                            background: "transparent",
                            border: "none",
                            color: "var(--pico-primary)",
                            cursor: "pointer",
                            fontSize: "0.8rem",
                            textDecoration: "underline",
                            padding: 0
                        }}
                    >
                        {t({ zh: "显示最新", en: "Latest" })}
                    </button>
                )}
                
                <button 
                    onClick={fetchPhotos}
                    title={t({ zh: "刷新", en: "Refresh" })}
                    style={{
                        background: "transparent",
                        border: "none",
                        color: "var(--pico-muted-color)",
                        cursor: "pointer",
                        marginLeft: "auto",
                        opacity: 0.8,
                        padding: "0.2rem"
                    }}
                >
                    <Icon name="refresh-cw" size={14} />
                </button>
            </div>
            <div class="farpov_timeline_scroll" ref={scrollRef}>
                {loading && <div class="farpov_timeline_loading">{t({ zh: "加载中...", en: "Loading..." })}</div>}
                
                {!loading && Object.entries(groupedPhotos).map(([date, groupPhotos]) => (
                    <div class="farpov_timeline_group" key={date}>
                        <div class="farpov_timeline_date">
                            <Icon name="calendar" size={12} style={{ marginRight: "0.4rem" }} />
                            {date}
                        </div>
                        <div class="farpov_timeline_items">
                            {groupPhotos.map((photo) => (
                                (() => {
                                    const isLeftSelected = selectedLeftPhotoId === photo.photo_id;
                                    const isRightSelected = selectedRightPhotoId === photo.photo_id;
                                    const isFocused = selectedPhotoId === photo.photo_id;
                                    return (
                                <TimelineItem 
                                    key={photo.photo_id} 
                                    photo={photo} 
                                    isFocused={isFocused}
                                    isLeftSelected={isLeftSelected}
                                    isRightSelected={isRightSelected}
                                    itemRef={(el) => {
                                        if (el) itemRefs.current.set(photo.photo_id, el);
                                        else itemRefs.current.delete(photo.photo_id);
                                    }}
                                    onSelect={(target) => {
                                        setSelectedPhotoId(photo.photo_id);
                                        onSelectPhoto(photo, target);
                                    }}
                                    onClick={() => {
                                        // Update selected date display without triggering re-fetch if possible
                                        // But here selectedDate drives the fetch. 
                                        // If we want to just "display" the date, we might need a separate display state or just set selectedDate.
                                        // Setting selectedDate will trigger fetchPhotos.
                                        const date = new Date(photo.photo_time * 1000);
                                        const y = date.getFullYear();
                                        const m = String(date.getMonth() + 1).padStart(2, '0');
                                        const d = String(date.getDate()).padStart(2, '0');
                                        const dateStr = `${y}-${m}-${d}`;
                                        
                                        if (selectedDate !== dateStr) {
                                            setSelectedDate(dateStr);
                                        }
                                        setSelectedPhotoId(photo.photo_id);
                                    }}
                                />
                                    );
                                })()
                            ))}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

const TimelineItem: FunctionComponent<{ 
    photo: PhotoInfo, 
    isFocused: boolean,
    isLeftSelected: boolean,
    isRightSelected: boolean,
    itemRef?: (el: HTMLDivElement | null) => void,
    onSelect: (target: 0 | 1) => void,
    onClick?: () => void
}> = ({ photo, isFocused, isLeftSelected, isRightSelected, itemRef, onSelect, onClick }) => {
    const [imgUrl, setImgUrl] = useState<string>("");
    
    useEffect(() => {
        let isMounted = true;
        
        getPhotoImageURL(photo.photo_id, "timeline")
            .then(url => {
                if (isMounted) setImgUrl(url);
            })
            .catch(err => {
                console.warn(`Failed to load thumbnail for photo ${photo.photo_id}`, err);
            });
            
        return () => { isMounted = false; };
    }, [photo.photo_id]);

    return (
        <div 
            class={`farpov_timeline_item ${isFocused ? "focused" : ""} ${isLeftSelected ? "selected-left" : ""} ${isRightSelected ? "selected-right" : ""}`}
            ref={itemRef}
            onClick={onClick}
        >
            <div class="farpov_timeline_thumb">
                {(isLeftSelected || isRightSelected) && (
                    <div class="farpov_timeline_side_badges">
                        {isLeftSelected && <span class="farpov_timeline_side_badge left">L</span>}
                        {isRightSelected && <span class="farpov_timeline_side_badge right">R</span>}
                    </div>
                )}
                {/* Use explicit width/height to force small rendering if CSS fails, 
                    but rely on CSS for actual layout. 
                    The backend thumbnail is already small (320px width usually). */}
                {imgUrl && <img src={imgUrl} alt={photo.photo_name} loading="lazy" width="160" height="90" />}
            </div>
            <div class="farpov_timeline_time">{formatTime(photo.photo_time)}</div>
            
            <div class="farpov_timeline_actions">
                <button 
                    type="button" 
                    class={`timeline_action_btn left ${isLeftSelected ? "active" : ""}`}
                    onClick={(e: Event) => { e.stopPropagation(); onSelect(0); }}
                    title="Set as Left/Bottom"
                >
                    L
                </button>
                <button 
                    type="button" 
                    class={`timeline_action_btn right ${isRightSelected ? "active" : ""}`}
                    onClick={(e: Event) => { e.stopPropagation(); onSelect(1); }}
                    title="Set as Right/Top"
                >
                    R
                </button>
            </div>
        </div>
    );
};
